import { Feedback } from "@deere/user-feedback-component";
import { Grid } from "@mui/material";

import { useOktaAuth } from "@okta/okta-react";
import React, { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import handlingUnitApi from "../../../../api/handlingUnit/handlingUnitApi";
import {
  ConfigContext,
  WindowWithAppConfig,
} from "../../../../context/ConfigContext";

// NOSONAR - This is a UI survey sampling check, not a security primitive.
function shouldRenderFeedbackSurvey(threshold: number) {
  const safeThreshold = Math.min(Math.max(threshold, 0), 1);

  if (safeThreshold === 0) {
    return false;
  }

  if (safeThreshold === 1) {
    return true;
  }

  const cryptoObject = globalThis.crypto;
  if (!cryptoObject || typeof cryptoObject.getRandomValues !== "function") {
    return false;
  }

  const randomValue =
    cryptoObject.getRandomValues(new Uint32Array(1))[0] / 4294967296;

  return randomValue < safeThreshold;
}

const QUAL_THRESHOLD = 0.0;
const FEEDBACK_THRESHOLD = 0.4;
const FEEDBACK_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;
export const TIME_UNTIL_FEEDBACK_POPS_UP_MS = 15000;

type SurveyDisplayTrigger = "unprompted";

function resetTimesSurveyIfNeeded() {
  const lastResetTimestamp = Number(
    globalThis.localStorage.getItem("lastResetTimestamp")
  );
  const now = Date.now();

  if (now - lastResetTimestamp >= FEEDBACK_COOLDOWN_MS) {
    globalThis.localStorage.setItem("timesSurveyHasBeenShown", "0");
    globalThis.localStorage.setItem("lastResetTimestamp", now.toString());
  }
}

function recordSurveyDisplay(
  trigger: SurveyDisplayTrigger,
  productId: string,
  route: string
) {
  const priorClientDisplayCount = Number(
    globalThis.localStorage.getItem("timesSurveyHasBeenShown")
  );

  // @ts-expect-error -- DD_RUM is expected on the window
  globalThis.DD_RUM?.addAction("FeedbackSurveyDisplayed", {
    trigger,
    productId,
    route,
    priorClientDisplayCount,
  });
}

function isEligibleForFeedbackSurvey(productId: string): boolean {
  const storedTimestamp = globalThis.localStorage.getItem(
    `feedbackV2-${productId}`
  );

  resetTimesSurveyIfNeeded();

  const timesSurveyHasBeenShown = Number(
    globalThis.localStorage.getItem("timesSurveyHasBeenShown")
  );

  if (timesSurveyHasBeenShown >= 2) {
    return false;
  }

  if (!storedTimestamp) {
    return true;
  }

  const timeSinceLastFeedback = Date.now() - Number(storedTimestamp);
  return timeSinceLastFeedback >= FEEDBACK_COOLDOWN_MS;
}

const UfcFeedback = () => {
  const { i18n } = useTranslation("common");
  const { HANDLING_UNIT_SERVICE_URL, ENABLE_FEEDBACK_COMPONENT } =
    useContext(ConfigContext);
  const { authState } = useOktaAuth();

  const getProductId = useCallback(() => {
    const appConfig = (window as WindowWithAppConfig).APP_CONFIG;
    const currentEnv = appConfig ? appConfig.NODE_ENV : undefined;

    switch (currentEnv) {
      case "prod":
        return "MID907";
      case "cert":
        return "MID907";
      case "devl":
        return "MID1270";
      case "qual":
        return "MID1270";
      default:
        return "MID1270";
    }
  }, []);

  const getEnvironment = useCallback(() => {
    const appConfig = (window as WindowWithAppConfig).APP_CONFIG;
    const currentEnv = appConfig ? appConfig.NODE_ENV : undefined;

    switch (currentEnv) {
      case "prod":
        return "production" as const;
      case "devl":
        return "development" as const;
      case "cert":
        return "cert" as const;
      case "qual":
        return "qual" as const;
      default:
        return "development" as const;
    }
  }, []);

  const apiFn = useCallback(
    (token: string): Promise<string> =>
      handlingUnitApi.getUFCToken(HANDLING_UNIT_SERVICE_URL, token),
    [HANDLING_UNIT_SERVICE_URL]
  );

  const openFeedbackPopup = useCallback(() => {
    if (!authState?.accessToken?.accessToken) {
      return;
    }

    apiFn(authState.accessToken.accessToken)
      .then((ufcToken) => {
        const feedback = new Feedback();
        const environment = getEnvironment();
        const productId = getProductId();
        const userTypeId =
          environment === "production" || environment === "cert" ? 3 : 1;

        const languageMap: { [key: string]: string } = {
          en_US: "en_US",
          es_MX: "es_ES",
          pt_BR: "pt_BR",
          zh_CN: "zh_CN",
          fr_CA: "fr_FR",
          de_DE: "de_DE",
          it_IT: "it_IT",
          pl_PL: "pl_PL",
        };

        feedback
          .popUpFunction({
            env: environment,
            userId: userTypeId,
            token: ufcToken,
            productId,
            language: languageMap[i18n.language] ?? "en_US",
            adminComments: "Triggered from JDPO feedback component",
          })
          .then((response) => {
            console.log("Feedback response: ", response);
          })
          .catch((error) => {
            console.error("Feedback error: ", error);
          });
      })
      .catch((error) => {
        console.error("Feedback token error: ", error);
      });
  }, [
    apiFn,
    authState?.accessToken?.accessToken,
    getEnvironment,
    getProductId,
    i18n.language,
  ]);

  useEffect(() => {
    if (!ENABLE_FEEDBACK_COMPONENT || !authState?.isAuthenticated) {
      return;
    }

    const productId = getProductId();
    if (!isEligibleForFeedbackSurvey(productId)) {
      return;
    }

    globalThis.localStorage.setItem(
      `feedbackV2-${productId}`,
      Date.now().toString()
    );

    const environment = getEnvironment();

    const effectiveThreshold =
      environment === "qual" ? QUAL_THRESHOLD : FEEDBACK_THRESHOLD;

    if (!shouldRenderFeedbackSurvey(effectiveThreshold)) {
      return;
    }

    const route = window.location.pathname.replace(/^\//, "") || "home";
    const timerId = window.setTimeout(() => {
      recordSurveyDisplay("unprompted", productId, route);
      const currentTimesShown = Number(
        globalThis.localStorage.getItem("timesSurveyHasBeenShown")
      );
      globalThis.localStorage.setItem(
        "timesSurveyHasBeenShown",
        (currentTimesShown + 1).toString()
      );
      openFeedbackPopup();
    }, TIME_UNTIL_FEEDBACK_POPS_UP_MS);

    // eslint-disable-next-line consistent-return
    return () => {
      window.clearTimeout(timerId);
    };
  }, [
    ENABLE_FEEDBACK_COMPONENT,
    authState?.isAuthenticated,
    getEnvironment,
    getProductId,
    openFeedbackPopup,
  ]);

  return <Grid alignSelf="center" paddingRight="8px" />;
};

export default UfcFeedback;
